import { childrenNamed, elementChildren, firstChild, numAttr, parsePoint, textOf } from './dom';
import type { AlignmentDegradation, KeyPoint, KeyPointKind, P3, StationEquation } from './types';

const MAX_CHORD_DEVIATION = 0.02; // m
const MAX_SEGMENT_ANGLE = (2 * Math.PI) / 180; // 2°
const MAX_ARC_SEGMENTS = 512;

/** Radius (average of the two declared-endpoint distances) + normalised sweep angle for an arc about `center`. */
function arcRadiusAndSweep(start: P3, end: P3, center: [number, number], ccw: boolean): { r: number; sweep: number } {
  const r = (Math.hypot(start[0] - center[0], start[1] - center[1]) + Math.hypot(end[0] - center[0], end[1] - center[1])) / 2;
  const a0 = Math.atan2(start[1] - center[1], start[0] - center[0]);
  const a1 = Math.atan2(end[1] - center[1], end[0] - center[0]);
  let sweep = ccw ? a1 - a0 : a0 - a1;
  while (sweep <= 1e-12) sweep += 2 * Math.PI;
  while (sweep > 2 * Math.PI) sweep -= 2 * Math.PI;
  return { r, sweep };
}

/**
 * True (analytic) arc length — used for stationing so key-point stations
 * reflect the design curve, not the chord-length sum of its tessellation.
 */
function arcAnalyticLength(start: P3, end: P3, center: [number, number], ccw: boolean): number {
  const { r, sweep } = arcRadiusAndSweep(start, end, center, ccw);
  return r * sweep;
}

/** Points along a circular arc from `start` to `end` about `center` (excluding `start`). */
function tessellateArc(start: P3, end: P3, center: [number, number], ccw: boolean): P3[] {
  const { r, sweep } = arcRadiusAndSweep(start, end, center, ccw);
  if (r < 1e-6) return [end];
  const a0 = Math.atan2(start[1] - center[1], start[0] - center[0]);
  const maxAngle = Math.min(MAX_SEGMENT_ANGLE, 2 * Math.acos(Math.max(-1, 1 - MAX_CHORD_DEVIATION / r)));
  let n = Math.min(MAX_ARC_SEGMENTS, Math.max(1, Math.ceil(sweep / maxAngle)));
  // Keep the segment count even so a sample always lands exactly on the arc's
  // angular midpoint (t=0.5) — needed for consumers that snap to it (e.g. a
  // picked point exactly at the bisector), and harmless everywhere else.
  if (n % 2 !== 0) n = Math.min(MAX_ARC_SEGMENTS, n + 1);
  const out: P3[] = [];
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const a = a0 + (ccw ? 1 : -1) * sweep * t;
    out.push([center[0] + r * Math.cos(a), center[1] + r * Math.sin(a), start[2] + (end[2] - start[2]) * t]);
  }
  out[out.length - 1] = end; // snap exactly onto the declared end
  return out;
}

/** Centre from radius + chord + rotation (+ optional declared length to pick the long arc). */
function arcCenterFromRadius(start: P3, end: P3, radius: number, ccw: boolean, length: number | null): [number, number] | null {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const c = Math.hypot(dx, dy);
  if (c < 1e-9 || radius < c / 2 - 1e-6) return null;
  const h = Math.sqrt(Math.max(0, radius * radius - (c * c) / 4));
  const mx = start[0] + dx / 2;
  const my = start[1] + dy / 2;
  const lx = -dy / c; // left normal
  const ly = dx / c;
  const longArc = length !== null && length > Math.PI * radius;
  const side = (ccw ? 1 : -1) * (longArc ? -1 : 1);
  return [mx + side * h * lx, my + side * h * ly];
}

/**
 * Clothoid from `start` to `end`. Integrates heading θ(s) = k0·s + (k1−k0)·s²/(2L)
 * in a local frame, then rotates so the local chord matches the world chord —
 * exact regardless of <PI>. Falls back to a straight chord on mismatch.
 */
function tessellateSpiral(start: P3, end: P3, length: number, rStart: number, rEnd: number, ccw: boolean): P3[] | null {
  if (!(length > 0)) return null;
  const k0 = Number.isFinite(rStart) && rStart > 0 ? 1 / rStart : 0;
  const k1 = Number.isFinite(rEnd) && rEnd > 0 ? 1 / rEnd : 0;
  const sgn = ccw ? 1 : -1;
  const samples = Math.max(8, Math.ceil(length / 2));
  const sub = 8; // integration sub-steps per sample
  const ds = length / (samples * sub);
  const xs = new Float64Array(samples + 1);
  const ys = new Float64Array(samples + 1);
  let x = 0, y = 0, s = 0;
  let prevC = 1, prevS = 0;
  for (let i = 1; i <= samples * sub; i++) {
    s += ds;
    const th = sgn * (k0 * s + ((k1 - k0) * s * s) / (2 * length));
    const c = Math.cos(th), sn = Math.sin(th);
    x += ((c + prevC) / 2) * ds;
    y += ((sn + prevS) / 2) * ds;
    prevC = c;
    prevS = sn;
    if (i % sub === 0) {
      xs[i / sub] = x;
      ys[i / sub] = y;
    }
  }
  const localChord = Math.atan2(ys[samples], xs[samples]);
  const worldChord = Math.atan2(end[1] - start[1], end[0] - start[0]);
  const rot = worldChord - localChord;
  const cr = Math.cos(rot), sr = Math.sin(rot);
  const out: P3[] = [];
  for (let i = 1; i <= samples; i++) {
    const t = i / samples;
    out.push([start[0] + xs[i] * cr - ys[i] * sr, start[1] + xs[i] * sr + ys[i] * cr, start[2] + (end[2] - start[2]) * t]);
  }
  const last = out[out.length - 1];
  if (Math.hypot(last[0] - end[0], last[1] - end[1]) > 0.5) return null;
  out[out.length - 1] = end;
  return out;
}

type GeomElementKind = 'Line' | 'Curve' | 'Spiral';

/** Parse <CoordGeom> into a polyline plus key points (internal stations). */
export function parseCoordGeomDetailed(
  coordGeom: Element,
): { points: P3[]; keyPoints: KeyPoint[]; warnings: string[]; degradations: AlignmentDegradation[]; length: number } {
  const points: P3[] = [];
  const keyPoints: KeyPoint[] = [];
  const warnings: string[] = [];
  // One entry per chord fallback below. Same events as `warnings`, but
  // structured and carried on the parse result — the console line alone left
  // the user with a corner-cutting alignment and no way to know.
  const degradations: AlignmentDegradation[] = [];
  let station = 0;
  let prevKind: GeomElementKind | null = null;

  // Push tessellated points onto the polyline WITHOUT advancing `station` —
  // station is tracked separately below using each element's true (analytic)
  // length, not the chord-length sum of its tessellation. Key-point stations
  // must reflect the design curve regardless of tessellation density.
  const push = (pts: P3[]) => {
    for (const p of pts) points.push(p);
  };
  const transition = (from: GeomElementKind | null, to: GeomElementKind): KeyPointKind | null => {
    if (from === null) return null;
    if (from === 'Line' && to === 'Curve') return 'PC';
    if (from === 'Curve' && to === 'Line') return 'PT';
    if (from === 'Line' && to === 'Spiral') return 'TS';
    if (from === 'Spiral' && to === 'Curve') return 'SC';
    if (from === 'Curve' && to === 'Spiral') return 'CS';
    if (from === 'Spiral' && to === 'Line') return 'ST';
    return null;
  };

  for (const el of elementChildren(coordGeom)) {
    const kind = el.localName as GeomElementKind;
    if (kind !== 'Line' && kind !== 'Curve' && kind !== 'Spiral') continue;
    const startTxt = textOf(firstChild(el, 'Start'));
    const endTxt = textOf(firstChild(el, 'End'));
    if (!startTxt || !endTxt) continue;
    const start = parsePoint(startTxt);
    const end = parsePoint(endTxt);
    if (points.length === 0) points.push(start);
    const kp = transition(prevKind, kind);
    if (kp) keyPoints.push({ station, kind: kp });
    const piTxt = textOf(firstChild(el, 'PI'));
    if (piTxt && kind !== 'Line') keyPoints.push({ station, kind: 'PI' });

    const chordLen = Math.hypot(end[0] - start[0], end[1] - start[1]);
    if (kind === 'Line') {
      push([end]);
      station += chordLen;
    } else if (kind === 'Curve') {
      const ccw = (el.getAttribute('rot') || 'cw').toLowerCase() === 'ccw';
      const centerTxt = textOf(firstChild(el, 'Center'));
      let center: [number, number] | null = null;
      if (centerTxt) {
        const c = parsePoint(centerTxt);
        center = [c[0], c[1]];
      } else {
        const radius = numAttr(el, 'radius');
        center = radius ? arcCenterFromRadius(start, end, radius, ccw, numAttr(el, 'length')) : null;
      }
      if (center) {
        push(tessellateArc(start, end, center, ccw));
        station += arcAnalyticLength(start, end, center, ccw);
      } else {
        warnings.push('Curve without usable centre/radius — drawn as chord');
        degradations.push({ kind: 'CURVE_CHORD', element: 'Curve', staInternalM: station, lengthM: chordLen });
        push([end]);
        station += chordLen;
      }
    } else {
      const ccw = (el.getAttribute('rot') || 'cw').toLowerCase() === 'ccw';
      const spiType = (el.getAttribute('spiType') || 'clothoid').toLowerCase();
      const length = numAttr(el, 'length');
      const rStart = numAttr(el, 'radiusStart') ?? Infinity;
      const rEnd = numAttr(el, 'radiusEnd') ?? Infinity;
      const pts = spiType === 'clothoid' && length ? tessellateSpiral(start, end, length, rStart, rEnd, ccw) : null;
      if (pts) {
        push(pts);
        station += length!; // a clothoid's declared length IS its analytic arc length
      } else {
        warnings.push(`Spiral (${spiType}) not supported — drawn as chord`);
        degradations.push({
          kind: 'SPIRAL_CHORD',
          element: `Spiral (${spiType})`,
          staInternalM: station,
          lengthM: chordLen,
        });
        push([end]);
        station += chordLen;
      }
    }
    prevKind = kind;
  }
  return { points, keyPoints, warnings, degradations, length: station };
}

/**
 * Parse <StaEquation> children into our 0-based internal stations.
 *
 * LandXML 1.2 defines `staInternal` as "the station value with NO equations
 * applied", i.e. `staStart + distance-along-the-alignment`
 * (landxml.org, LandXML-1.2Doc_StaEquation). Our `StationEquation.staInternal`
 * is a plain 0-based distance from the first vertex, so we subtract `staStart`.
 *
 * `staBack` is only a fallback: it is the *display* station immediately BEHIND
 * the equation, expressed in the stationing that is in force there. For the
 * first equation that stationing is `staStart`-based, so `staBack - staStart`
 * is exact; for the 2nd and later equations it is the PREVIOUS equation's
 * ahead stationing, which we cannot undo without knowing the whole chain — so
 * only the first equation's fallback is reliable. Prefer `staInternal`.
 */
export function parseStationEquations(alignment: Element, staStart: number): StationEquation[] {
  const out: StationEquation[] = [];
  for (const eq of childrenNamed(alignment, 'StaEquation')) {
    const ahead = numAttr(eq, 'staAhead');
    if (ahead === null) continue;
    const declared = numAttr(eq, 'staInternal');
    let internal: number;
    if (declared !== null) {
      internal = declared - staStart;
    } else {
      const back = numAttr(eq, 'staBack');
      if (back === null) continue;
      internal = back - staStart;
    }
    out.push({ staInternal: internal, staAhead: ahead });
  }
  return out.sort((a, b) => a.staInternal - b.staInternal);
}
